import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { AppCacheStatus, getIsReady } from '@fullstory/redux-util';
import {
  createArrayShallowEqualSelector,
  createDeepEqualSelector,
} from '@fullstory/redux-util/utils/selectors';
import { createSelector } from 'reselect';
import { AppState } from '../../types';

const PageDefZero: PageDefinition = {
  id: 0,
  friendlyId: '0',
  name: 'Not defined',
  description: '',
  archived: false,
  labels: [],
  patterns: [],
  isUserDefined: false,
  createdBy: '',
  createdAt: null,
  updatedBy: '',
  updatedAt: null,
  expiration: null,
  metadata: null,
  suggestion: null,
};

const emptyPagesById = new Map<number, PageDefinition>();

const getPageFlowDataMap = (state: AppState) => state.pages.pageMetrics;
type PageFlowDataMap = ReturnType<typeof getPageFlowDataMap>;

export const getPagesRequestState = (state: AppState) => state.pages.pages;

export const makeGetPageFlowDataArray = (storageIds: string[]) =>
  createArrayShallowEqualSelector(
    createSelector(getPageFlowDataMap, (map: PageFlowDataMap) =>
      storageIds.map(s => getPageFlowStatus(map, s)),
    ),
    dataArray => dataArray,
  );

function getPageFlowStatus(map: PageFlowDataMap, storageId: string) {
  const {
    status = AppCacheStatus.Initial,
    ready = false,
    data = null,
    safeMessage = null,
  } = map[storageId] ?? {};

  return { status, ready, data, storageId, safeMessage };
}

export const pagesById = (state: AppState): Map<number, PageDefinition> =>
  state.pages.pages.data?.pages || emptyPagesById;

export const pagesByIdWithPageDefZero = createDeepEqualSelector(getPagesRequestState, pages =>
  pages.data?.pages ? new Map([...pages.data.pages.entries(), [0, PageDefZero]]) : emptyPagesById,
);

export const getAllPagesLoading = (state: AppState) => state.pages.getAllPageRequest.loading;

// only return non archived pages
export const activePages = createSelector(pagesById, pages => {
  if (!pages) {
    return [];
  }

  const list: PageDefinition[] = [];
  for (const [_, page] of pages) {
    if (!page.archived) list.push(page);
  }
  return list;
});

export const allPages = createSelector(pagesById, pages => {
  if (!pages) {
    return [];
  }

  const list: PageDefinition[] = [];
  for (const [_, page] of pages) {
    list.push(page);
  }
  return list;
});

export const makeGetPage = (pageId: number) =>
  createSelector(pagesById, pages => pages.get(pageId));

export const allPagesWithPageDefZero = createSelector(pagesByIdWithPageDefZero, pages => {
  if (!pages) {
    return [];
  }

  const list: PageDefinition[] = [];
  for (const [_, page] of pages) {
    list.push(page);
  }
  return list;
});

export const hasNativeMobileAndroidPages = createSelector(allPages, pages =>
  pages.some(p =>
    p.labels.some(l =>
      l.platforms.some(p => p.platform === NativePlatform.NATIVE_PLATFORM_ANDROID),
    ),
  ),
);

export const hasNativeMobileIosPages = createSelector(allPages, pages =>
  pages.some(p =>
    p.labels.some(l => l.platforms.some(p => p.platform === NativePlatform.NATIVE_PLATFORM_IOS)),
  ),
);

export const urlMatches = (state: AppState) => state.pages.pages.data?.urlMatches;

export const pendingSuggestedPages = (state: AppState) =>
  state.pages.getAllPageRequest.data?.pendingSuggestedPages || [];

export const getAllPagesReady = (state: AppState) => getIsReady(state.pages.getAllPageRequest);
